import React from "react";
import { useState, useRef, useEffect } from "react";
import { r2 } from "../lib/constants.js";
import { SildymasTab } from "./SildymasTab.jsx";

export function KonfiguratorModal({ layers, rooms, markings, setMarkings, templates, setTemplates,
          templateGroups, setTemplateGroups, calibration, getScale, selectedDrawingId, zoom,
          floorZones, setFloorZones, floorZonesVisible, setFloorZonesVisible, roomCeilingHeight,
          getLayerHeat, setLayerHeat, onOpenTemplateEditor,
          onStartFloorZone, onClose }) {

          const [activeLayerTab, setActiveLayerTab] = useState(layers[0]?.id || null);

          // Lango judinimas — tempiama už antraštės
          const [dragOffset, setDragOffset] = useState({x:0,y:0});
          const dragStateRef = useRef(null);
          function handleHeaderMouseDown(e) {
            if (e.button !== 0) return;
            dragStateRef.current = { startX: e.clientX, startY: e.clientY, offsetX: dragOffset.x, offsetY: dragOffset.y };
          }
          useEffect(() => {
            function onMove(e) {
              if (!dragStateRef.current) return;
              const { startX, startY, offsetX, offsetY } = dragStateRef.current;
              setDragOffset({ x: offsetX + (e.clientX - startX), y: offsetY + (e.clientY - startY) });
            }
            function onUp() { dragStateRef.current = null; }
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
            return () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
          }, []);

          // Užtikrinti šablono grupę
          function ensureTemplateGroup(groupName) {
            if (!templateGroups.includes(groupName))
              setTemplateGroups(prev => [...prev, groupName]);
          }

          // Rasti arba sukurti šabloną pagal pavadinimą
          function ensureTemplate(name, groupName, shape, color, strokeWidthM) {
            const existing = templates.find(t => t.name === name);
            if (existing) return existing;
            ensureTemplateGroup(groupName);
            const scale = getScale();
            const strokeWidthPx = (scale && strokeWidthM) ? strokeWidthM / scale : 4;
            const nt = {
              id: Date.now() + Math.random(),
              name, groupName, shape,
              color: color || '#735DFF',
              strokeWidth: Math.max(1, Math.round(strokeWidthPx)),
              size: 16,
              positions: [],
            };
            setTemplates(prev => [...prev, nt]);
            return nt;
          }

          // Kolektoriaus šablonas
          function ensureCollectorTemplate(contours, qty) {
            const name = `KOL DEB ${contours}ž.`;
            const groupName = 'Šildymo kolektoriai';
            const existing = templates.find(t => t.name === name);
            if (existing) {
              // Visada sinchronizuoti pozicijas — pridėti jei nėra, atnaujinti kiekį
              setTemplates(prev => prev.map(t => {
                if (t.name !== name) return t;
                const hasPos = t.positions && t.positions.length > 0;
                const newPos = {
                  id: Date.now() + Math.random(),
                  name, groupName: t.groupName, groupId: null,
                  unit: 'vnt.', qtyPerUnit: qty != null ? qty : 1, price: 0,
                };
                if (!hasPos) return { ...t, positions: [newPos] };
                return { ...t, positions: t.positions.map((p,i) => i===0 ? {...p, unit:'vnt.', qtyPerUnit: qty != null ? qty : p.qtyPerUnit} : p) };
              }));
              return existing;
            }
            ensureTemplateGroup(groupName);
            const nt = {
              id: Date.now() + Math.random(),
              name, groupName, shape: 'rect',
              color: '#dc2626', strokeWidth: 2, size: 20,
              positions: [{
                id: Date.now() + Math.random() + 1,
                name, groupName, groupId: null,
                unit: 'vnt.', qtyPerUnit: qty != null ? qty : 1, price: 0,
              }],
            };
            setTemplates(prev => [...prev, nt]);
            return nt;
          }

          // Vamzdžio šablonas (apskritimas, 1px, m)
          function ensurePipeTemplate(customName, qty) {
            const name = customName || 'VM PE-RT D18';
            const groupName = 'Vandentiekio vamzdynas';
            const existing = templates.find(t => t.name === name);
            if (existing) {
              // Visada sinchronizuoti pozicijas — pridėti jei nėra, atnaujinti kiekį ir vnt.
              setTemplates(prev => prev.map(t => {
                if (t.name !== name) return t;
                const hasPos = t.positions && t.positions.length > 0;
                const newPos = {
                  id: Date.now() + Math.random(),
                  name, groupName: t.groupName, groupId: null,
                  unit: 'm', qtyPerUnit: qty != null ? qty : 1, price: 0,
                };
                if (!hasPos) return { ...t, shape: 'circle', strokeWidth: 1, positions: [newPos] };
                return { ...t, positions: t.positions.map((p,i) => i===0 ? {...p, unit:'m', qtyPerUnit: qty != null ? qty : p.qtyPerUnit} : p) };
              }));
              return existing;
            }
            ensureTemplateGroup(groupName);
            const nt = {
              id: Date.now() + Math.random(),
              name, groupName, shape: 'circle',
              color: '#1e88e5', strokeWidth: 1, size: 6,
              positions: [{
                id: Date.now() + Math.random() + 1,
                name, groupName, groupId: null,
                unit: 'm', qtyPerUnit: qty != null ? qty : 1, price: 0,
              }],
            };
            setTemplates(prev => [...prev, nt]);
            return nt;
          }

          const activeLayer = layers.find(l => l.id === activeLayerTab);

          return (
            <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:400}}
              onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
              <div onClick={e=>e.stopPropagation()} style={{background:'#f5f6fa',borderRadius:14,width:'min(900px,96vw)',maxHeight:'90vh',
                boxShadow:'0 20px 60px rgba(0,0,0,0.25)',display:'flex',flexDirection:'column',overflow:'hidden',
                transform:`translate(${dragOffset.x}px,${dragOffset.y}px)`}}>

                {/* Antraštė — tempiama judinti langą */}
                <div onMouseDown={handleHeaderMouseDown}
                  style={{background:'#fff',borderBottom:'1px solid #e5e7eb',padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,cursor:'move',userSelect:'none'}}>
                  <span style={{fontSize:16,fontWeight:900,color:'#1c1c1c',letterSpacing:0.3}}>⚙ Konfigūratorius</span>
                  <button onClick={onClose} onMouseDown={e=>e.stopPropagation()} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#9ca3af',lineHeight:1}}>×</button>
                </div>

                {/* Lapai pagal sluoksnius */}
                <div style={{display:'flex',flex:1,overflow:'hidden'}}>

                  {/* Kairė: sluoksnių tabs */}
                  <div style={{width:180,flexShrink:0,background:'#fff',borderRight:'1px solid #e5e7eb',overflowY:'auto',padding:'8px 0'}}>
                    {layers.length === 0 && (
                      <div style={{padding:'16px 12px',fontSize:11,color:'#9ca3af',textAlign:'center'}}>
                        Nėra sluoksnių.<br/>Sukurkite sluoksnius brėžinyje.
                      </div>
                    )}
                    {layers.map(layer => {
                      const isAct = layer.id === activeLayerTab;
                      return (
                        <div key={layer.id} onClick={()=>setActiveLayerTab(layer.id)}
                          style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',cursor:'pointer',
                            borderLeft:'3px solid '+(isAct?layer.color:'transparent'),
                            background:isAct?layer.color+'12':'transparent'}}>
                          <div style={{width:10,height:10,borderRadius:'50%',background:layer.color,flexShrink:0}}/>
                          <span style={{fontSize:12,fontWeight:isAct?800:500,color:isAct?layer.color:'#374151'}}>{layer.label}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Dešinė: aktyvaus lapo turinys */}
                  <div style={{flex:1,overflowY:'auto',padding:'20px'}}>
                    {!activeLayer && (
                      <div style={{textAlign:'center',color:'#9ca3af',paddingTop:40}}>
                        <div style={{fontSize:32,marginBottom:8}}>⚙</div>
                        <div>Pasirinkite sluoksnį kairėje</div>
                      </div>
                    )}
                    {activeLayer && activeLayer.systemKey === 'sildymas' && (
                      <SildymasTab layer={activeLayer} rooms={rooms}
                        markings={markings} setMarkings={setMarkings}
                        ensureCollectorTemplate={ensureCollectorTemplate}
                        ensurePipeTemplate={ensurePipeTemplate}
                        roomCeilingHeight={roomCeilingHeight}
                        getScale={getScale} calibration={calibration}
                        selectedDrawingId={selectedDrawingId}
                        templates={templates}
                        floorZones={floorZones.filter(z => z.layerId === activeLayer.id)}
                        allFloorZones={floorZones}
                        setFloorZones={setFloorZones}
                        floorZonesVisible={floorZonesVisible}
                        setFloorZonesVisible={setFloorZonesVisible}
                        heatSettings={getLayerHeat(activeLayer.id)}
                        onHeatSettingsChange={(patch) => setLayerHeat(activeLayer.id, patch)}
                        onStartFloorZone={()=>onStartFloorZone(activeLayer.id)}
                        onOpenTemplateEditor={onOpenTemplateEditor}
                        onAddRoomAsZone={(room) => {
                          const zone = {
                            id: Date.now(),
                            drawingId: selectedDrawingId,
                            layerId: activeLayer.id,
                            label: room.name,
                            points: room.points ? [...room.points] : [],
                            areaPx: room.areaPx,
                            areaM2: room.areaM2,
                          };
                          setFloorZones(prev => [...prev, zone]);
                        }}
                        onMergeRooms={(selectedRoomIds, mergedName) => {
                          const selected = rooms.filter(r => selectedRoomIds.includes(r.id));
                          const totalArea = r2(selected.reduce((a,r)=>a+(r.areaM2||0),0));
                          const zone = {
                            id: Date.now(),
                            drawingId: selectedDrawingId,
                            layerId: activeLayer.id,
                            label: mergedName || selected.map(r=>r.name).join(' + '),
                            points: [],
                            areaPx: null,
                            areaM2: totalArea,
                            mergedFrom: selectedRoomIds,
                          };
                          setFloorZones(prev => [...prev, zone]);
                        }}
                      />
                    )}
                    {activeLayer && activeLayer.systemKey !== 'sildymas' && (
                      <div style={{textAlign:'center',color:'#9ca3af',paddingTop:40}}>
                        <div style={{fontSize:28,marginBottom:8}}>🔧</div>
                        <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>{activeLayer.label}</div>
                        <div style={{fontSize:12}}>Šio sluoksnio konfigūracija bus pridėta vėliau.</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }
